import 'leaflet/dist/leaflet.css'

import L from 'leaflet'
import FullScreen from 'leaflet.fullscreen'
import { renderToString } from 'react-dom/server'
import { useEffect, useRef } from 'react'
import { WoWtoLatLng, type MapContent, type MapPOI } from '../utils'
import PlotOccupantLabel from './PlotOccupantLabel'
import styles from './HousingMap.module.scss'
import type { DisplayOccupant } from './types'

type Props = {
  mapId: number
  neighborhood: MapContent
  openPlot: MapPOI | null
  occupantsByPlot: Record<string, DisplayOccupant[]>
  user?: wowaudit.User
  userHasApprovedPlot: boolean
  userHasClaimedPlot: boolean
  shardIndex: number
  hoveringPlot: string | null
  setHoveringPlot: (plot: string | null) => void
  onOpenPlot: (plot: MapPOI | null) => void
}

const NeighborhoodMap = ({
  mapId,
  neighborhood,
  openPlot,
  occupantsByPlot,
  user,
  userHasApprovedPlot,
  userHasClaimedPlot,
  shardIndex,
  hoveringPlot,
  setHoveringPlot,
  onOpenPlot,
}: Props) => {
  const mapRef = useRef<L.Map | null>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const plotMarkersRef = useRef<
    Map<string, { marker: L.Marker; labelMarker: L.Marker; plot: MapPOI }>
  >(new Map())
  const layerGroupsRef = useRef<{
    plotLabelLayerGroup?: L.LayerGroup
    plotNumberLayerGroup?: L.LayerGroup
  }>({})

  const createMarkerIcon = (
    occupants: DisplayOccupant[],
    isHovered = false,
  ) => {
    const claimed = occupants.find((occupant) => occupant.status === 'claimed')
    const approved = occupants.find(
      (occupant) => occupant.status === 'approved',
    )
    const userId = user?.id
    const userChoice =
      userId &&
      occupants.find((occupant) => occupant.character.userId === userId)
    const likedChoice =
      userHasClaimedPlot && userId && claimed?.character.userId === userId
        ? 1
        : (userHasApprovedPlot || userHasClaimedPlot) &&
          userId &&
          approved?.character.userId === userId
        ? approved.ranked_choice
        : !(userHasApprovedPlot || userHasClaimedPlot) && userChoice
        ? userChoice.ranked_choice
        : 0

    return L.icon({
      iconUrl: `https://data.wowaudit.com/img/housing/icon_house${
        likedChoice > 0 ? `_choice_${likedChoice}` : ''
      }.png`,
      className: `${likedChoice > 0 || claimed ? '' : 'grayscale'} ${
        isHovered ? 'hover' : ''
      }`.trim(),
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    })
  }

  const filterOccupantsForPlot = (occupants: DisplayOccupant[]) => {
    return occupants.filter(
      (occupant) =>
        occupant.status !== 'rejected' &&
        occupant.shard_index === shardIndex &&
        !(
          (occupant.character.userHasApprovedPlot ||
            occupant.character.userHasClaimedPlot) &&
          occupant.status === 'requested'
        ),
    )
  }

  const createLabelIcon = (content: string, isHovered = false) => {
    return L.divIcon({
      html: content,
      className: `plot-number-label ${isHovered ? 'hover' : ''}`.trim(),
      iconSize: [30, 20],
      iconAnchor: [0, -12],
    })
  }

  const createOccupantLabel = (
    occupants: DisplayOccupant[],
    isHovered = false,
  ) => {
    return createLabelIcon(
      renderToString(
        <div className='plot-marker-label requested'>
          <PlotOccupantLabel occupants={occupants} />
        </div>,
      ),
      isHovered,
    )
  }

  const setupHoverHandlers = (marker: L.Marker, plotName: string) => {
    marker.on('mouseover', () => setHoveringPlot(plotName))
    marker.on('mouseout', () => setHoveringPlot(null))
  }

  useEffect(() => {
    if (!mapContainerRef.current) return

    const map = L.map(mapContainerRef.current, {
      center: [0, 0],
      zoom: 1,
      minZoom: 2,
      maxZoom: 7,
      crs: L.CRS.Simple,
      zoomControl: false,
      preferCanvas: true,
      worldCopyJump: false,
      attributionControl: true,
      zoomDelta: 0.5,
      zoomSnap: 0.1,
      wheelPxPerZoomLevel: 100,
      zoomAnimation: false,
    })
    const worldMapLayer = new L.LayerGroup()
    const latLngX = WoWtoLatLng(
      map,
      mapId,
      neighborhood.BoundingBox.Item1.X,
      neighborhood.BoundingBox.Item1.Y,
    )
    const latLngY = WoWtoLatLng(
      map,
      mapId,
      neighborhood.BoundingBox.Item2.X,
      neighborhood.BoundingBox.Item2.Y,
    )
    const bounds = new L.LatLngBounds(
      (latLngX ?? [0, 0]) as L.LatLngExpression,
      (latLngY ?? [0, 0]) as L.LatLngExpression,
    )

    worldMapLayer.addLayer(
      L.imageOverlay(neighborhood.ImageURL, bounds, { interactive: false }),
    )
    map.addLayer(worldMapLayer)

    const plotLayerGroup = new L.LayerGroup()
    const plotLabelLayerGroup = new L.LayerGroup()
    const plotNumberLayerGroup = new L.LayerGroup()
    map.addLayer(plotLayerGroup)
    map.addLayer(plotNumberLayerGroup)
    map.addLayer(plotLabelLayerGroup)
    layerGroupsRef.current.plotLabelLayerGroup = plotLabelLayerGroup
    layerGroupsRef.current.plotNumberLayerGroup = plotNumberLayerGroup

    const overlays = {
      'Plot icons': plotLayerGroup,
      'Plot numbers': plotNumberLayerGroup,
      Occupants: plotLabelLayerGroup,
    }
    L.control
      .layers(undefined, overlays, { collapsed: false, position: 'topright' })
      .addTo(map)

    const storageKey = 'neighborhood-map-layers'
    const savedLayerState = localStorage.getItem(storageKey)
    if (savedLayerState) {
      try {
        const layerState = JSON.parse(savedLayerState) as Record<
          string,
          boolean
        >
        Object.entries(overlays).forEach(([name, layerGroup]) => {
          if (layerState[name] === false) {
            map.removeLayer(layerGroup)
          }
        })
      } catch {
        localStorage.removeItem(storageKey)
      }
    }

    const saveLayerState = () => {
      localStorage.setItem(
        storageKey,
        JSON.stringify(
          Object.fromEntries(
            Object.entries(overlays).map(([name, layerGroup]) => [
              name,
              map.hasLayer(layerGroup),
            ]),
          ),
        ),
      )
    }
    map.on('overlayadd overlayremove', saveLayerState)
    new L.Control.Zoom({ position: 'topleft' }).addTo(map)
    new FullScreen({ position: 'topleft' }).addTo(map)
    map.attributionControl.setPrefix('')
    map.attributionControl.addAttribution(
      `Map by <a href="https://housing.wowdb.com/neighborhoods/${
        mapId === 1 ? 'alliance' : 'horde'
      }" target="_blank" rel="noopener noreferrer">WowDB</a>`,
    )
    mapRef.current = map

    const fitMapToViewport = () => {
      map.invalidateSize()
      map.fitBounds(bounds, {
        padding: [-250, -250],
        maxZoom: map.getMaxZoom(),
      })

      if (mapId === 2) {
        const southWest = bounds.getSouthWest()
        const currentCenter = map.getCenter()
        const currentZoom = map.getZoom()
        const currentBounds = map.getBounds()
        const visibleMapHeight =
          currentBounds.getNorth() - currentBounds.getSouth()

        map.setView(
          [southWest.lat + visibleMapHeight / 2, currentCenter.lng],
          currentZoom,
          { animate: false },
        )
      }
    }

    requestAnimationFrame(() => {
      fitMapToViewport()
      setTimeout(fitMapToViewport, 100)
    })

    let resizeTimeout: ReturnType<typeof setTimeout> | null = null
    const handleResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(fitMapToViewport, 150)
    }
    window.addEventListener('resize', handleResize)

    neighborhood.POIs.forEach((plot) => {
      if (!['Portal', 'Plot'].includes(plot.Type)) return

      const latLng = WoWtoLatLng(map, mapId, Number(plot.X), Number(plot.Y))
      const marker = L.marker((latLng ?? [0, 0]) as L.LatLngExpression, {
        interactive: plot.Type === 'Plot',
        keyboard: false,
      })

      if (plot.Type === 'Portal') {
        marker.setIcon(
          L.icon({
            iconUrl: neighborhood.PortalURL,
            iconSize: [32, 32],
          }),
        )
      }

      if (plot.Type === 'Plot') {
        const occupants = filterOccupantsForPlot(
          occupantsByPlot[plot.Name] ?? [],
        )
        const handlePlotClick = () => onOpenPlot(plot)
        marker.setIcon(createMarkerIcon(occupants))
        marker.on('click', handlePlotClick)
        setupHoverHandlers(marker, plot.Name)

        const labelMarker = L.marker((latLng ?? [0, 0]) as L.LatLngExpression, {
          icon:
            occupants.length > 0
              ? createOccupantLabel(occupants)
              : createLabelIcon(
                  `<div class="plot-marker-label plot-number-only">${plot.Name}</div>`,
                ),
          interactive: true,
          keyboard: false,
        })
        labelMarker.on('click', handlePlotClick)
        setupHoverHandlers(labelMarker, plot.Name)
        if (occupants.length > 0) {
          plotLabelLayerGroup.addLayer(labelMarker)
        } else {
          plotNumberLayerGroup.addLayer(labelMarker)
        }
        plotMarkersRef.current.set(plot.Name, { marker, labelMarker, plot })
      }

      plotLayerGroup.addLayer(marker)
    })

    return () => {
      window.removeEventListener('resize', handleResize)
      if (resizeTimeout) clearTimeout(resizeTimeout)
      map.remove()
      mapRef.current = null
      plotMarkersRef.current.clear()
    }
  }, [mapId, neighborhood])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const { plotLabelLayerGroup, plotNumberLayerGroup } = layerGroupsRef.current
    if (!plotLabelLayerGroup || !plotNumberLayerGroup) return

    plotMarkersRef.current.forEach(
      ({ marker, labelMarker, plot }, plotName) => {
        const occupants = filterOccupantsForPlot(
          occupantsByPlot[plotName] ?? [],
        )
        const isHovered =
          hoveringPlot === plotName || openPlot?.Name === plotName
        const hasOccupants = occupants.length > 0

        marker.setIcon(createMarkerIcon(occupants, isHovered))

        if (hasOccupants) {
          if (plotNumberLayerGroup.hasLayer(labelMarker)) {
            plotNumberLayerGroup.removeLayer(labelMarker)
            plotLabelLayerGroup.addLayer(labelMarker)
          }
          labelMarker.setIcon(createOccupantLabel(occupants, isHovered))
        } else {
          if (plotLabelLayerGroup.hasLayer(labelMarker)) {
            plotLabelLayerGroup.removeLayer(labelMarker)
            plotNumberLayerGroup.addLayer(labelMarker)
          }
          labelMarker.setIcon(
            createLabelIcon(
              `<div class="plot-marker-label plot-number-only">${plot.Name}</div>`,
              isHovered,
            ),
          )
        }
      },
    )
  }, [hoveringPlot, occupantsByPlot, openPlot, shardIndex])

  return <div ref={mapContainerRef} className={styles.neighborhoodMap} />
}

export default NeighborhoodMap
